import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { useBrandAuthCheck } from "../../hooks/useBrandAuthCheck";
import BlurOverlay from "./BlurOverlay";
import ClaimStatusBadge from "./ClaimStatusBadge";
import ClaimProfileModal from "./ClaimProfileModal";
import RegisterModal from "../../components/RegisterModal";
import { useDispatch } from "react-redux";
import { clearUserState } from "../../Redux/userSlice";
import {
  formatInfluencerRating,
  getEffectiveInfluencerRating,
} from "../utils/rating";

const InfluencerCard = memo(({ influencer, blur }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isBrand, isAuthenticated, isInfluencer } = useBrandAuthCheck();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);

  if (!influencer) return null;

  const {
    username,
    // title,
    name,
    image,
    location,
    rating,
    reviewCount,
    price,
    isVerified,
    isSaved,
    contentType,
    gender,
  } = influencer;

  const showClaimButton =
    !isBrand &&
    (isInfluencer || !isAuthenticated) &&
    !isVerified;

  const canClaim = !isVerified;
  const displayRating = formatInfluencerRating(
    getEffectiveInfluencerRating({
      rating,
      reviewCount,
      isVerified,
    })
  );

  const handleCardClick = (e) => {
    e.preventDefault();

    if (!isAuthenticated || !isBrand) {
      setShowJoinModal(true);
      return;
    }

    if (blur) {
      navigate("/pricing");
      return;
    }

    navigate(`/marketplace/influencer/${username}`);
  };

  const handleClaimClick = (e) => {
    e.stopPropagation();
    if (!canClaim) return;
    if (!isAuthenticated && !isInfluencer) {
      navigate(`/influencer/login?redirect=/marketplace/influencer/${username}`);
      return;
    }
    if (isBrand) return;
    setShowClaimModal(true);
  };

  const handleRegisterAs = (type) => {
    setShowJoinModal(false);
    if (type === "influencer") {
      navigate("/influencer/login");
      return;
    }

    const userType = localStorage.getItem("userType");
    if (userType === "influancer") {
      dispatch(clearUserState());
    }
    navigate("/company/login");
  };

  // const handleCardClick = (e) => {
  //   e.preventDefault();
  //   navigate(`/marketplace/influencer/${username}`);
  // };


  return (
    <>
      <div
        onClick={handleCardClick}
        className="block bg-white rounded-xl shadow-sm hover:shadow-md transition overflow-hidden relative cursor-pointer"
      >
        {blur && <BlurOverlay influencerUsername={username} />}

        <div className="absolute top-3 right-3 flex items-center gap-2 z-20">
          {showClaimButton && (
            <button
              type="button"
              onClick={handleClaimClick}
              disabled={!canClaim}
              className={`text-xs font-medium px-3 py-1.5 rounded-full shadow ${canClaim
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-amber-100 text-amber-800 cursor-default"
                }`}
            >
              Claim Profile
            </button>
          )}
          <button
            type="button"
            className="bg-white/90 p-1.5 rounded-full shadow"
            onClick={(e) => e.stopPropagation()}
            aria-label={isSaved ? "Saved influencer" : "Save influencer"}
          >
            <Heart
              size={18}
              className={isSaved ? "text-red-500 fill-red-500" : "text-gray-700"}
            />
          </button>
        </div>

        <div className="relative hover:scale-105 transition-all duration-200">
          <img
            src={image || "https://via.placeholder.com/400x240?text=Influencer"}
            alt={name || "Influencer"}
            loading="lazy"
            decoding="async"
            className="w-full h-52 sm:h-60 object-cover"
          />

          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />

          {/* {ugc && (
            <span className="absolute bottom-9 left-3 bg-white/95 text-xs px-2 py-1 rounded-full shadow">
              📷 UGC
            </span>
          )} */}

          <div className="absolute bottom-3 left-3 right-3 flex justify-between items-center">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {username || name || "—"}
              </p>
              {isVerified && <ClaimStatusBadge isVerified />}
            </div>
            <span className="text-white text-sm shrink-0">⭐ {displayRating}</span>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {Array.isArray(contentType) && contentType.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {contentType.slice(0, 2).map((type, idx) => (
                    <span
                      key={`${type}-${idx}`}
                      className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full"
                    >
                      {String(type || "").trim()}
                    </span>
                  ))}
                  {contentType.length > 2 && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                      +{contentType.length - 2} more
                    </span>
                  )}
                </div>
              ) : (
                <div className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full inline-block">
                  <span className="text-sm">{contentType ?? "Content Type not specified"}</span>
                </div>
              )}
            </div>
            <span className="text-xs font-bold whitespace-nowrap ">
              ${price ?? "—"}
            </span>
            
          </div>


          {/* <p className="text-sm text-gray-400 mt-1 truncate">
          <FaMapMarkerAlt className="text-gray-400" />
            {location || "—"}
          </p> */}
          <div className="flex items-center gap-1 text-gray-500 text-sm mt-2">
            <FaMapMarkerAlt className="text-gray-400" />
            <span>{location || 'Location not specified'}</span>
          </div>
          <span className="text-xs text-gray-500 font-bold mt-2">
              {gender ?? "Gender not specified"}
            </span>
        </div>
      </div>

      <RegisterModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onRegisterAs={handleRegisterAs}
      />

      <ClaimProfileModal
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        username={username}
        onSuccess={() => {
          setShowClaimModal(false);
          window.location.reload();
        }}
      />
    </>
  );
});

InfluencerCard.displayName = "InfluencerCard";
export default InfluencerCard;




// import { memo } from "react";
// import { Heart } from "lucide-react";

// const InfluencerCard = memo(({ influencer }) => {
//   if (!influencer) return null;
//   const { name, image, location, rating, price, ugc } = influencer;
//   return (
//     <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition overflow-hidden relative">
//       <button type="button" className="absolute top-3 right-3 bg-white p-1 rounded-full shadow z-10" aria-label="Save">
//         <Heart size={18} />
//       </button>

//       <img
//         src={image || "https://via.placeholder.com/400x240?text=Influencer"}
//         alt={name || "Influencer"}
//         loading="lazy"
//         decoding="async"
//         className="w-full h-52 sm:h-60 object-cover"
//       />

//       <div className="p-4">
//         {ugc && (
//           <span className="text-xs bg-gray-100 px-2 py-1 rounded">UGC</span>
//         )}

//         <h3 className="mt-2 font-semibold truncate">{name || "—"}</h3>
//         <p className="text-sm text-gray-500 truncate">{location || "—"}</p>

//         <div className="flex items-center justify-between mt-3">
//           <span className="text-sm">⭐ {rating != null ? rating : "—"}</span>
//           <span className="font-semibold">${price != null ? price : "—"}</span>
//         </div>
//       </div>
//     </div>
//   );
// });
// InfluencerCard.displayName = "InfluencerCard";

// export default InfluencerCard;
